import {
  DataProtectionAssessmentDto,
  DPAAnswerDto,
  DPAApprovalPartyDto,
  DPAPromptDto,
  DPAType,
} from "../../common/service/server/dto/DataProtectionAssessmentDto";
import { NetworkRequestState } from "../../common/models";
import { useCallback } from "react";
import { Api } from "../../common/service/Api";
import { useDataRequest } from "../../common/hooks/api";

const dpaQueryKey = (dpaId: string) => ["dpa", dpaId];

export const useDpa = (
  organizationId: string,
  dpaId: string,
): [DataProtectionAssessmentDto | undefined, NetworkRequestState, () => Promise<void>] => {
  const apiRequest = useCallback(
    () => Api.dataProtectionAssessment.getDpa(organizationId, dpaId),
    [organizationId, dpaId],
  );

  const {
    result: dpa,
    request,
    refresh: fetch,
  } = useDataRequest({
    queryKey: dpaId ? dpaQueryKey(dpaId) : [],
    api: apiRequest,
    notReady: !organizationId || !dpaId,
  });

  return [dpa, request, fetch];
};

export const useDpas = (
  organizationId: string,
): [DataProtectionAssessmentDto[], NetworkRequestState, () => Promise<void>] => {
  const apiRequest = useCallback(
    () => Api.dataProtectionAssessment.getDpas(organizationId),
    [organizationId],
  );

  const {
    result: dpas,
    request,
    refresh,
  } = useDataRequest({
    queryKey: ["dpas", organizationId],
    api: apiRequest,
    notReady: !organizationId,
    dependencies: [organizationId],
  });

  return [dpas, request, refresh];
};

export const useSuggestedDpas = (
  organizationId: string,
): [Record<DPAType, boolean>, NetworkRequestState, () => Promise<void>] => {
  const apiRequest = useCallback(
    () => Api.dataProtectionAssessment.getSuggestedDpas(organizationId),
    [organizationId],
  );

  const {
    result: suggestedDpas,
    request,
    refresh,
  } = useDataRequest({
    queryKey: ["suggestedDpas", organizationId],
    api: apiRequest,
    notReady: !organizationId,
    dependencies: [organizationId],
  });

  return [suggestedDpas, request, refresh];
};

export const useDpaPrompts = (
  organizationId: string,
): [DPAPromptDto[], NetworkRequestState, () => Promise<void>] => {
  const apiRequest = useCallback(
    () => Api.dataProtectionAssessment.getDpaPrompts(organizationId),
    [organizationId],
  );

  const {
    result: prompts,
    request,
    refresh,
  } = useDataRequest({
    queryKey: ["dpaPrompts", organizationId],
    api: apiRequest,
    notReady: !organizationId,
    dependencies: [organizationId],
  });

  return [prompts, request, refresh];
};

export const useDpaAnswers = (
  organizationId: string,
  dpaId: string,
): [DPAAnswerDto[], NetworkRequestState, () => Promise<void>] => {
  const apiRequest = useCallback(
    () => Api.dataProtectionAssessment.getDpaAnswers(organizationId, dpaId),
    [organizationId, dpaId],
  );

  const {
    result: prompts,
    request,
    refresh,
  } = useDataRequest({
    queryKey: ["dpaAnswers", dpaId],
    api: apiRequest,
    notReady: !organizationId || !dpaId,
    dependencies: [organizationId, dpaId],
  });

  return [prompts, request, refresh];
};

export const useDpaApprovalParties = (
  organizationId: string,
  dpaId: string,
): [DPAApprovalPartyDto[], NetworkRequestState, () => Promise<void>] => {
  const apiRequest = useCallback(
    () => Api.dataProtectionAssessment.getDpaApprovalParties(organizationId, dpaId),
    [organizationId, dpaId],
  );

  const {
    result: approvalParties,
    request,
    refresh,
  } = useDataRequest({
    queryKey: ["dpaApprovalParties", dpaId],
    api: apiRequest,
    dependencies: [organizationId, dpaId],
  });

  return [approvalParties, request, refresh];
};
